import { z } from "zod/v4";
import { Moneda } from "@/shared/types/moneda";
import { Stack, Typography } from "@mui/material";
import { getMonedaBase } from "../shared/utils";
import { Form, FormProps, RegisterField, useFormContext, useStore } from "@/components/form";
import { NumberField } from "@/components/inputs";

function roundToDecimal(x: number, d: number) {
    const pow10 = Math.pow(10, d);
    return Math.round(x * pow10) / pow10;
}

export const editCotizacionFormSchema = z.object({
    inverseDisplay: z.boolean().default(true),
    cotizacionData: z.record(z.string(), z.number().gt(0, "El valor debe ser mayor que 0."))
})

interface EditCotizacionFormProps extends FormProps<z.input<typeof editCotizacionFormSchema>> {
    monedas: Moneda[]
}

export default function EditCotizacionForm({
    monedas,
    form,
    ...props
}: EditCotizacionFormProps) {
    const monedaBase = getMonedaBase(monedas);

    return <Form form={form} gap={3} {...props}>
        <Stack marginLeft={-2} alignSelf={"flex-end"}>
            <Form.CheckBox name="inverseDisplay" label="Invertir Valores" />
        </Stack>

        {monedas.map(m => m.monedaBase ?
            <MonedaPrincipalField key={m.id} moneda={m} /> :
            <MonedasSecundariaField key={m.id} moneda={m} monedaBase={monedaBase} />)}

        <Form.SubmitButton>Guardar</Form.SubmitButton>
    </Form>
}

function MonedaPrincipalField({ moneda }: { moneda: Moneda }) {
    return <Stack gap={2} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
        <Stack flex={1}>
            <Typography>{moneda.nombre} ({moneda.simbolo})</Typography>
        </Stack>
        <Stack flex={1} marginRight={1.5}>
            <Typography color="text.secondary" textAlign="right">Principal</Typography>
        </Stack>
    </Stack>
}

function MonedasSecundariaField({ moneda, monedaBase }: { moneda: Moneda, monedaBase?: Moneda }) {
    const form = useFormContext();
    const inverseDisplay = useStore(form.store, state => state.values.inverseDisplay);

    return <Stack gap={2} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
        <Stack flex={1}>
            <Typography>{moneda.nombre} ({moneda.simbolo})</Typography>
        </Stack>
        <Stack flex={1}>
            <RegisterField name={`cotizacionData[${moneda.id}]`}>
                {field => <NumberField
                    //Form Props
                    value={roundToDecimal(Number((inverseDisplay && field.value) ? 1 / field.value : field.value), 10)}
                    onValueChange={({ floatValue }) => {
                        if (floatValue == null || field.value === "") field.handleChange("")
                        else if (floatValue === 0) field.handleChange(0)
                        else !inverseDisplay ?
                            field.handleChange(Number(floatValue)) :
                            field.handleChange(Number(1 / floatValue));
                    }}

                    inputRef={field.ref}
                    disabled={field.disabled}
                    onBlur={field.handleBlur}
                    onKeyDown={field.handleKeyDown}
                    error={field.hasError}
                    helperText={field.errorMessage}

                    //Display Props
                    label="Valor"
                    slotProps={{
                        input: {
                            endAdornment: monedaBase && <Typography textAlign={"right"} whiteSpace={"nowrap"}>
                                &nbsp;{inverseDisplay ? moneda.simbolo : monedaBase.simbolo}
                            </Typography>
                        }
                    }}
                    alignRight
                    decimalScale={10}
                />}
            </RegisterField>
        </Stack>
    </Stack>
}